/*
 * Katana Pro — player row marquee.
 * A transform-based marquee: the track drifts gently leftward and loops
 * seamlessly by wrapping the offset by one card-set width. Because it never
 * touches native scrollLeft, there is no momentum "force-stop" and no repaint
 * flash on wrap-around. Manual drag / swipe nudges the same offset, and the
 * drift pauses on hover or while the visitor interacts.
 * Progressive enhancement: with JS off, the track is a static flex row.
 */
(function () {
  'use strict';

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function now() {
    return window.performance && performance.now ? performance.now() : Date.now();
  }

  function initRow(track) {
    var row = track.parentElement;
    if (!row) return;
    var originals = Array.prototype.slice.call(track.children);
    if (!originals.length) return;

    var clones = [];
    var offset = 0; // current translateX (<= 0)
    var copyWidth = 0; // width of one original set incl. one trailing gap

    function apply() {
      track.style.transform = 'translate3d(' + offset + 'px, 0, 0)';
    }

    function wrap() {
      if (copyWidth <= 0) return;
      // Keep offset within (-copyWidth, 0] so we always have content either side.
      while (offset <= -copyWidth) offset += copyWidth;
      while (offset > 0) offset -= copyWidth;
    }

    function clearClones() {
      clones.forEach(function (c) { track.removeChild(c); });
      clones = [];
    }

    function cloneSet() {
      originals.forEach(function (el) {
        var c = el.cloneNode(true);
        c.classList.add('is-clone');
        c.setAttribute('aria-hidden', 'true');
        c.setAttribute('tabindex', '-1');
        track.appendChild(c);
        clones.push(c);
      });
    }

    function build() {
      clearClones();
      var vw = row.clientWidth;
      // Duplicate the set once, then measure a single set's footprint from the
      // first clone's position. Keep duplicating until the track comfortably
      // exceeds one viewport + one set so the loop point is never on-screen.
      cloneSet();
      copyWidth = clones.length ? clones[0].offsetLeft : 0;
      var guard = 0;
      while (copyWidth > 0 && track.scrollWidth < vw + copyWidth + 8 && guard < 16) {
        cloneSet();
        guard++;
      }
      offset = 0;
      apply();
    }

    // ---- Nearest-to-centre highlight (drives the mobile "selected" card) ----
    var lastSel = 0;
    function updateSelected(t) {
      if (t - lastSel < 120) return;
      lastSel = t;
      var rect = row.getBoundingClientRect();
      var center = rect.left + rect.width / 2;
      var cards = track.children;
      var best = null;
      var bestDist = Infinity;
      var i;
      for (i = 0; i < cards.length; i++) {
        var r = cards[i].getBoundingClientRect();
        var d = Math.abs(r.left + r.width / 2 - center);
        if (d < bestDist) { bestDist = d; best = cards[i]; }
      }
      for (i = 0; i < cards.length; i++) {
        cards[i].classList.toggle('is-selected', cards[i] === best);
      }
    }

    // ---- Auto drift ----
    var SPEED = reduced ? 0 : 20; // px/sec — gentle
    var paused = false;
    var pauseUntil = 0;
    var dragging = false;
    var last = 0;

    function pauseFor(ms) {
      var until = now() + ms;
      if (until > pauseUntil) pauseUntil = until;
    }

    function frame(t) {
      if (!last) last = t;
      var dt = t - last;
      last = t;
      if (SPEED && !paused && !dragging && now() >= pauseUntil && dt > 0 && dt < 100 && copyWidth > 0) {
        offset -= (SPEED * dt) / 1000;
        wrap();
        apply();
      }
      updateSelected(t);
      requestAnimationFrame(frame);
    }

    // ---- Hover pause (mouse only) ----
    row.addEventListener('mouseenter', function () { paused = true; });
    row.addEventListener('mouseleave', function () { paused = false; });

    // ---- Drag (mouse / pen) ----
    var lastX = 0;
    var moved = false;
    row.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return;
      dragging = true;
      moved = false;
      lastX = e.clientX;
      row.classList.add('is-dragging');
      e.preventDefault();
    });
    window.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - lastX;
      lastX = e.clientX;
      if (Math.abs(dx) > 2) moved = true;
      offset += dx;
      wrap();
      apply();
    });
    function endDrag() {
      if (!dragging) return;
      dragging = false;
      row.classList.remove('is-dragging');
      pauseFor(1500);
    }
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);

    // ---- Swipe (touch) — only hijack clearly-horizontal gestures ----
    var tStartX = 0;
    var tStartY = 0;
    var tLastX = 0;
    var horizontal = null;
    row.addEventListener('touchstart', function (e) {
      var tch = e.touches[0];
      tStartX = tLastX = tch.clientX;
      tStartY = tch.clientY;
      horizontal = null;
      pauseFor(2500);
    }, { passive: true });
    row.addEventListener('touchmove', function (e) {
      var tch = e.touches[0];
      if (horizontal === null) {
        var adx = Math.abs(tch.clientX - tStartX);
        var ady = Math.abs(tch.clientY - tStartY);
        if (adx < 6 && ady < 6) return;
        horizontal = adx > ady;
      }
      if (!horizontal) return; // let the page scroll vertically
      e.preventDefault();
      var dx = tch.clientX - tLastX;
      tLastX = tch.clientX;
      offset += dx;
      wrap();
      apply();
      pauseFor(2500);
    }, { passive: false });

    // Wheel with horizontal intent nudges the marquee (trackpads / shift-wheel).
    row.addEventListener('wheel', function (e) {
      var dx = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : (e.shiftKey ? e.deltaY : 0);
      if (!dx) return;
      e.preventDefault();
      offset -= dx;
      wrap();
      apply();
      pauseFor(1800);
    }, { passive: false });

    // Suppress the click that follows a drag so cards don't navigate.
    row.addEventListener('click', function (e) {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      }
    }, true);

    // ---- Boot + keep measurements fresh ----
    build();
    if (document.readyState !== 'complete') {
      window.addEventListener('load', build);
    }
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(build, 200);
    });

    requestAnimationFrame(frame);
  }

  function boot() {
    var tracks = document.querySelectorAll('[data-kpro-track]');
    Array.prototype.forEach.call(tracks, initRow);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
